'use client';

import { useState, useEffect, useCallback } from 'react';
import { HardDrive, FileText, Search, RefreshCw, FolderOpen, ChevronLeft, Check, X } from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import { getFileIcon } from '@/components/EmojiIcon';

interface NasFile { name: string; path: string; size?: number; }

interface NasProjectSelectorProps {
    agentType: string;
    accentBg?: string;
    accentText?: string;
    accentBorder?: string;
    gradient?: string;
    onAnalyze: (projectId: string, selectedFiles: string[], agentType: string) => void;
    isProcessing: boolean;
    children?: React.ReactNode; // 추가 옵션 (건물용도 등)
    initialPath?: string;
}

export function NasProjectSelector({
    agentType,
    gradient = 'from-indigo-600 to-purple-600',
    onAnalyze,
    isProcessing,
    children,
    initialPath,
}: NasProjectSelectorProps) {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const [nasProjects, setNasProjects] = useState<any[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    
    // 트리 탐색 상태
    const [currentPath, setCurrentPath] = useState('');
    const [folders, setFolders] = useState<{name: string; path: string}[]>([]);
    const [files, setFiles] = useState<{name: string; path: string; ext: string; size: number}[]>([]);
    
    const [selectedFiles, setSelectedFiles] = useState<{name: string; path: string}[]>([]);
    const [loadingNas, setLoadingNas] = useState(false);
    const [projectSearch, setProjectSearch] = useState('');
    const [folderSearch, setFolderSearch] = useState('');

    // 프로젝트 목록 로드
    useEffect(() => {
        fetch('/api/nas/projects')
            .then(r => r.json())
            .then(data => {
                if (data.connected && data.projects) {
                    setNasProjects(data.projects);
                    const ptk = data.projects.find((p: any) => p.id.includes('A프로젝트'));
                    if (ptk) setSelectedProjectId(ptk.id);
                }
            })
            .catch(() => {});
    }, []);

    const browseNasFolders = useCallback(async (path: string = '') => {
        setLoadingNas(true);
        try {
            const actualPath = path; // empty string allows NAS root browsing
            const res = await fetch('/api/project-board/resolve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'browse-nas', path: actualPath })
            });
            if (res.ok) {
                const data = await res.json();
                setFolders(data.folders || []);
                setFiles(data.files || []);
                setCurrentPath(data.path || '');
            } else {
                setFolders([]);
                setFiles([]);
            }
        } catch {
            setFolders([]);
            setFiles([]);
        } finally {
            setLoadingNas(false);
        }
    }, []);

    // 특정 프로젝트 선택 시 해당 폴더로 진입
    useEffect(() => {
        if (initialPath) {
            browseNasFolders(initialPath);
            return;
        }
        if (!selectedProjectId) return;
        const proj = nasProjects.find(p => p.id === selectedProjectId || p.id.includes(selectedProjectId));
        if (proj && proj.path) {
            browseNasFolders(proj.path);
        } else {
             // 기본 규칙으로 탐색 (fallbacks)
             const yyMatch = selectedProjectId.match(/^ID_(\d{2})/);
             const yy = yyMatch ? yyMatch[1] : '25';
             browseNasFolders(`B_DESIGN/[20${yy}_PROJECT]/${selectedProjectId}`);
        }
    }, [selectedProjectId, nasProjects, browseNasFolders, initialPath]);

    const toggleFile = (file: {name: string; path: string}) => {
        setSelectedFiles(prev =>
            prev.some(f => f.path === file.path) ? prev.filter(f => f.path !== file.path) : [...prev, file]
        );
    };

    const filteredProjects = projectSearch
        ? nasProjects.filter(p => p.id.toLowerCase().includes(projectSearch.toLowerCase()) || (p.name || '').toLowerCase().includes(projectSearch.toLowerCase()))
        : nasProjects;

    const inputClass = `w-full px-3 py-2 rounded-lg text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'}`;

    return (
        <>
            {/* 프로젝트 선택 */}
            <div className="mb-3">
                <label className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>NAS 프로젝트/폴더 탐색</label>
                <div className="relative mt-1.5 flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-500" />
                        <input value={projectSearch} onChange={e => setProjectSearch(e.target.value)} placeholder="프로젝트 검색..."
                            className={`${inputClass} pl-8`} />
                    </div>
                </div>
                <select value={selectedProjectId} onChange={e => setSelectedProjectId(e.target.value)}
                    className={`${inputClass} mt-1.5 mb-2`}>
                    <option value="">프로젝트 또는 폴더 선택...</option>
                    {filteredProjects.slice(0, 50).map(p => (
                        <option key={p.id} value={p.id}>{p.id} ({p.fileCount || 0}파일)</option>
                    ))}
                </select>
                
                {/* ── 탐색기 경로 표시 ── */}
                <div className={`p-2 rounded flex items-center justify-between mt-1 ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'} text-[10px] truncate`}>
                    <span className={`truncate mr-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} title={currentPath}>
                         {currentPath ? (
                             <>
                                 <button onClick={() => {
                                     const parent = currentPath.split('/').slice(0, -1).join('/');
                                     browseNasFolders(parent);
                                 }} className="opacity-70 hover:opacity-100 flex items-center hover:text-indigo-400 pr-1 inline">
                                     <ChevronLeft className="w-3.5 h-3.5 inline pb-0.5" />
                                 </button>
                                 {currentPath.split('/').pop()}
                             </>
                         ) : 'NAS 전체'}
                    </span>
                    <button onClick={() => browseNasFolders(currentPath)} disabled={loadingNas} className="text-gray-400 hover:text-indigo-400">
                        <RefreshCw className={`w-3.5 h-3.5 ${loadingNas ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* 파일 및 폴더 목록 */}
            {(folders.length > 0 || files.length > 0) && (
                <div>
                    <label className={`text-[10px] font-bold uppercase tracking-wider flex justify-between ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        <span>탐색 결과 ({folders.length} 폴더, {files.length} 파일)</span>
                        <span className="text-indigo-500">{selectedFiles.length}개 선택됨</span>
                    </label>
                    <div className="relative mt-1 mb-1.5">
                        <input value={folderSearch} onChange={e => setFolderSearch(e.target.value)} placeholder="현재 폴더 내 검색..."
                            className={`w-full px-3 py-1.5 rounded-lg text-[10px] ${isDark ? 'bg-black/20 border border-white/5 text-white' : 'bg-gray-50 border border-gray-200 text-gray-900'}`} />
                    </div>

                    <div className={`max-h-[220px] overflow-y-auto rounded-lg border custom-scrollbar ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        {/* 폴더 목록 */}
                        {folders.filter(f => f.name.toLowerCase().includes(folderSearch.toLowerCase())).map((folder) => (
                            <div key={folder.path}
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('application/json', JSON.stringify({
                                        type: 'nas_folder',
                                        path: folder.path || folder.name,
                                        name: folder.name
                                    }));
                                }}
                                onClick={() => browseNasFolders(folder.path)}
                                className={`flex items-center gap-2 px-2.5 py-1.5 text-[10px] cursor-pointer transition ${isDark ? 'hover:bg-white/5 border-b border-white/5 text-gray-300' : 'hover:bg-gray-50 border-b border-gray-100 text-gray-700'}`}>
                                <FolderOpen className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                <span className="truncate">{folder.name}</span>
                            </div>
                        ))}
                        
                        {/* 파일 목록 */}
                        {files.filter(f => f.name.toLowerCase().includes(folderSearch.toLowerCase())).map(f => {
                            const isSelected = selectedFiles.some(sel => sel.path === f.path);
                            const FileIcon = getFileIcon(f.ext);
                            const sizeStr = f.size > 1048576 
                                ? `${(f.size / 1048576).toFixed(1)}MB`
                                : f.size > 1024 
                                    ? `${(f.size / 1024).toFixed(0)}KB`
                                    : `${f.size}B`;

                            return (
                                <label key={f.path} 
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('application/json', JSON.stringify({
                                            type: 'nas_file',
                                            path: f.path || f.name,
                                            name: f.name
                                        }));
                                    }}
                                    className={`flex items-center gap-2 px-2.5 py-1.5 text-[10px] cursor-pointer transition ${isDark ? 'hover:bg-white/5 border-b border-white/5 text-gray-300' : 'hover:bg-gray-50 border-b border-gray-100 text-gray-700'}`}>
                                    <input type="checkbox" checked={isSelected} onChange={() => toggleFile(f)}
                                        className="w-3 h-3 rounded accent-indigo-500" />
                                    <span className="flex-shrink-0 flex items-center justify-center w-5">
                                        <FileIcon className="w-3.5 h-3.5 text-slate-400" />
                                    </span>
                                    <span className="truncate flex-1">{f.name}</span>
                                    <span className={`text-[9px] flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{sizeStr}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 선택 내역 하단 바 */}
            {selectedFiles.length > 0 && (
                <div className={`mt-2 mb-3 p-2 rounded-lg text-[10px] ${isDark ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-200'}`}>
                    <div className="font-bold flex items-center gap-1 mb-1 text-indigo-500">
                        <Check className="w-3 h-3" /> 선택된 파일
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {selectedFiles.map(sf => (
                            <span key={sf.path} className={`px-1.5 py-0.5 rounded flex items-center gap-1 ${isDark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                                <span className="truncate max-w-[120px]">{sf.name}</span>
                                <button onClick={() => toggleFile(sf)} className="hover:text-rose-400 text-indigo-400/50"><X className="w-2.5 h-2.5" /></button>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* 추가 옵션 (children) */}
            {children}

            {/* 분석 버튼 */}
            <button onClick={() => {
                if (selectedFiles.length === 0 && selectedProjectId) {
                    // 선택 파일이 없으면 기존 로직대로 진행 (프로젝트 전체)
                    onAnalyze(selectedProjectId, [], agentType);
                } else if (selectedFiles.length > 0) {
                     // 선택된 파일들의 path를 전달
                    onAnalyze(selectedProjectId || 'Custom', selectedFiles.map(f => f.path), agentType);
                }
            }} disabled={isProcessing || (selectedFiles.length === 0 && !selectedProjectId)}
                className={`mt-4 w-full py-3 rounded-xl bg-gradient-to-r ${gradient} text-white text-xs font-bold cursor-pointer disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg`}>
                {isProcessing ? <><RefreshCw className="w-4 h-4 animate-spin" />NAS 분석 중...</> : <><FolderOpen className="w-4 h-4" />{selectedFiles.length > 0 ? `${selectedFiles.length}개 파일 분석 시작` : 'NAS 프로젝트 전체 분석'}</>}
            </button>
        </>
    );
}

/** 모드 전환 버튼 (NAS/수동) */
export function AgentModeToggle({
    mode,
    onModeChange,
    accentBg = 'bg-indigo-500/20',
    accentText = 'text-indigo-400',
    accentBorder = 'border-indigo-500/30',
}: {
    mode: 'manual' | 'nas';
    onModeChange: (m: 'manual' | 'nas') => void;
    accentBg?: string;
    accentText?: string;
    accentBorder?: string;
}) {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const inactiveClass = isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200';

    return (
        <div className="flex gap-1.5 mb-3">
            <button onClick={() => onModeChange('nas')}
                className={`flex-1 py-2 rounded-lg text-[11px] font-bold cursor-pointer transition flex items-center justify-center gap-1 ${mode === 'nas' ? `${accentBg} ${accentText} border ${accentBorder}` : inactiveClass}`}>
                <HardDrive className="w-3 h-3" /> NAS 프로젝트
            </button>
            <button onClick={() => onModeChange('manual')}
                className={`flex-1 py-2 rounded-lg text-[11px] font-bold cursor-pointer transition flex items-center justify-center gap-1 ${mode === 'manual' ? `${accentBg} ${accentText} border ${accentBorder}` : inactiveClass}`}>
                <FileText className="w-3 h-3" /> 수동 입력
            </button>
        </div>
    );
}

/** NAS SSE 스트리밍 분석 호출 훅 */
export function useNasAnalysis() {
    const [aiStream, setAiStream] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [aiDone, setAiDone] = useState(false);

    const analyze = useCallback(async (projectId: string, selectedFiles: string[], agentType: string, query?: string) => {
        setIsProcessing(true);
        setAiStream('');
        setAiDone(false);

        try {
            const res = await fetch('/api/ai-tools/analyze', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    agent: agentType,
                    projectId,
                    files: selectedFiles.length > 0 ? selectedFiles : undefined,
                    query: query || '',
                }),
            });

            if (!res.ok) throw new Error('API 오류');
            const reader = res.body?.getReader();
            if (!reader) throw new Error('스트림 없음');
            const decoder = new TextDecoder();
            let fullText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split('\n')) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.substring(6));
                            if (data.type === 'chunk') { fullText += data.text; setAiStream(fullText); }
                            else if (data.type === 'complete') { fullText = data.fullText; setAiStream(fullText); }
                        } catch { }
                    }
                }
            }
            setAiDone(true);
            return fullText;
        } catch (err: any) {
            setAiStream(`오류: ${err.message}`);
            setAiDone(true);
            return '';
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const reset = useCallback(() => {
        setAiStream('');
        setAiDone(false);
        setIsProcessing(false);
    }, []);

    return { aiStream, isProcessing, aiDone, analyze, reset, setAiStream };
}
